import type { FC } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import cn from 'classnames';

import { imgSize200 } from 'constants/image';
import useMedia from 'hooks/useMedia';
import { formatImagelink } from 'utils';
import { DateUtils } from 'utils/DateUtils';
import { getTestIdByParts } from 'utils/getTestIdByParts';
import { formatOfferPrice, formatPrice } from 'utils/price';
import type { WithTestId } from 'types/app';

import { ModalNamesEnum } from '@Modules/app/constants';
import { openModal } from '@Modules/app/slice';
import { selectUser } from '@Modules/auth/selector';
import type { OfferType, TranslationType } from '@Modules/offers/types';

import { OfferOwner } from '@Atomic/organisms/offer/OfferOwner';
import { Portal } from '@Atomic/organisms/Portal';
import { SellerContact } from '@Atomic/organisms/SellerContact';
import { formatDiscount } from '@Atomic/molecules/FeedCard/utils';
import { GoodPriceLink } from '@Atomic/molecules/GoodPrice/GoodPriceLink';
import { SellerBadges } from '@Atomic/molecules/SellerBadges';
import { Avatar } from '@Atomic/atoms/Avatar';
import { ClassicBadge } from '@Atomic/atoms/Badges/ClassicBadge';
import { Title } from '@Atomic/atoms/Headline';
import { Text } from '@Atomic/atoms/Text';

import styles from './style.module.scss';

type OfferDetailsProps = WithTestId<{
  offer: OfferType;
  translation?: TranslationType;
  isExpired: boolean;
  variant?: 'default' | 'no-contacts';
  className?: string;
}>;

export const OfferDetails: FC<OfferDetailsProps> = ({
  offer,
  isExpired,
  translation,
  className,
  variant = 'default',
  testId = 'offer-details',
}) => {
  const { md, lg } = useMedia();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const user = useSelector(selectUser);

  const isDesktop = md || lg;
  const PanelWrapper = !isDesktop ? Portal : React.Fragment;
  const isOwner = Boolean(offer?.seller.uuid && user?.uuid && user.uuid === offer.seller.uuid);

  const showDiscount = Boolean(offer.grossPriceDiscount && offer.grossPrice);
  // Contacts panel renders regardless of variant — it's the mobile bottom-sheet
  // (variant='no-contacts' is used on mobile to suppress only the inline seller
  // info card, since that card lives in a separate <SellerDetails /> section
  // on mobile).
  const showContacts = !isExpired;

  const handleDeliveryModalOpen = (): void => {
    dispatch(
      openModal({
        name: ModalNamesEnum.DOWNLOAD_APP,
        payload: {
          source: 'delivery',
        },
      })
    );
  };

  if (!offer) {
    return null;
  }

  // Figma: desktop sidebar puts the link under the price/discount block, mobile & tablet above
  // the price (BP-8273). `isDesktop` hydrates from the server-detected device, so SSR matches.
  const goodPriceLink = offer.goodPrice ? (
    <GoodPriceLink
      className={styles.goodPriceLink}
      testId={getTestIdByParts(testId, 'good-price')}
    />
  ) : null;

  return (
    <div
      className={cn(styles.offerDetails, { [styles.expired]: isExpired }, className)}
      data-testid={testId}
    >
      <section
        className={cn(styles.offerInfo, {
          [styles.noDiscount]: !showDiscount,
          [styles.withGoodPrice]: !!goodPriceLink,
        })}
      >
        <div className={styles.offerPriceSection}>
          {!isDesktop && goodPriceLink}

          <div className={styles.offerPrice} data-testid={getTestIdByParts(testId, 'price')}>
            {formatOfferPrice(offer.price, offer.priceType, t)}
          </div>

          {showDiscount && (
            <div className={styles.offerDiscount}>
              <Text
                className={styles.oldPriceValue}
                testId={getTestIdByParts(testId, 'gross-price')}
              >
                {formatPrice(offer.grossPrice!, t)}
              </Text>

              <Text className={styles.discountValue} testId={getTestIdByParts(testId, 'discount')}>
                {formatDiscount(offer.grossPriceDiscount!)}
              </Text>
            </div>
          )}

          {isDesktop && goodPriceLink}
        </div>

        <Title.H1 className={styles.offerTitle} testId={getTestIdByParts(testId, 'title')}>
          {translation?.title || offer.title}
        </Title.H1>

        {(offer.delivery || offer.courierDelivery || offer.urgentSale) && (
          <div className={styles.badges}>
            {offer.delivery && (
              <ClassicBadge
                intent="neutral"
                variant="outline"
                size="md"
                icon="track"
                label={offer.delivery.label}
                rounded={false}
                onClick={handleDeliveryModalOpen}
                testId={getTestIdByParts(testId, 'birbir-delivery')}
              />
            )}
            {offer.courierDelivery && (
              <ClassicBadge
                intent="neutral"
                variant="outline"
                size="md"
                icon="box"
                label={t('badge.delivery')}
                rounded={false}
                testId={getTestIdByParts(testId, 'courier-delivery')}
              />
            )}
            {offer.urgentSale && (
              <ClassicBadge
                intent="neutral"
                variant="outline"
                size="md"
                icon="flash"
                label={t('badge.urgentSale')}
                rounded={false}
                testId={getTestIdByParts(testId, 'urgent-sale')}
              />
            )}
          </div>
        )}
      </section>
      {variant !== 'no-contacts' && (
        <>
          <hr className={styles.separator} />
          <div className={styles.seller}>
            <OfferOwner seller={offer.seller} withLinks variant="short" />
            <SellerBadges
              business={offer.seller.business}
              agency={offer.seller.agency}
              proSeller={offer.seller.proSeller}
              verified={offer.seller.verified}
              isOwner={isOwner}
              testId={getTestIdByParts(testId, 'seller-badges')}
            />
          </div>
        </>
      )}
      {showContacts && (
        <PanelWrapper>
          <div
            className={styles.sellerContacts}
            data-testid={getTestIdByParts(testId, 'seller-contacts')}
          >
            {!isDesktop && offer.seller.activity && (
              <div
                className={styles.profileMini}
                data-testid={getTestIdByParts(testId, 'profile-mini')}
              >
                <div className={styles.profileMiniInfo}>
                  <span className={styles.profileMiniNameRow}>
                    <Text
                      className={styles.profileMiniName}
                      testId={getTestIdByParts(testId, 'profile-mini-name')}
                    >
                      {offer.seller.name || t('offer.guest')}
                    </Text>
                    {offer.seller.activity.online && (
                      <Text
                        className={styles.profileMiniOnline}
                        testId={getTestIdByParts(testId, 'profile-mini-online')}
                      >
                        {t('offer.online')}
                      </Text>
                    )}
                  </span>
                  {offer.seller.activity.responseTimeTitle && (
                    <Text
                      className={styles.profileMiniResponseTime}
                      testId={getTestIdByParts(testId, 'profile-mini-response-time')}
                    >
                      {offer.seller.activity.responseTimeTitle}
                    </Text>
                  )}
                </div>
                <div
                  className={cn(styles.profileMiniAvatar, {
                    [styles.profileMiniAvatarOnline]: offer.seller.activity.online,
                  })}
                >
                  <Avatar
                    src={
                      offer.seller.avatar
                        ? formatImagelink(offer.seller.avatar.cropUrlTemplate, imgSize200)
                        : ''
                    }
                    alt={offer.seller.name || 'seller'}
                    className={styles.profileMiniImage}
                  />
                </div>
              </div>
            )}
            <SellerContact
              offerId={offer.id}
              seller={offer.seller}
              contactViaApp={offer.contactViaApp}
            />
            {isDesktop && offer.seller.registeredDate && (
              <Text
                className={styles.registeredDate}
                testId={getTestIdByParts(testId, 'registered-date')}
              >
                {t('offer.onPlatform')}{' '}
                {DateUtils.format(offer.seller.registeredDate, 'DD.MM.YYYY')}
              </Text>
            )}
          </div>
        </PanelWrapper>
      )}
    </div>
  );
};
